import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faXmark } from '@fortawesome/free-solid-svg-icons';
import { askCortex } from '../../api/chat';
import { renderChatMarkdown } from './chatMarkdown';

function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const threadRef = useRef({ threadId: null, parentMessageId: 0 });

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    return undefined;
  }, [open]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, sending, open]);

  async function sendQuestion(text) {
    if (!text || sending) return;

    setDraft('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setSending(true);
    try {
      const data = await askCortex(text, threadRef.current);
      if (data.threadId != null) {
        threadRef.current = {
          threadId: data.threadId,
          parentMessageId: data.parentMessageId ?? 0,
        };
      }
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: data.answer || 'No answer returned.',
        suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
        placeholder: Boolean(data.placeholder),
      }]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: err?.message || 'Could not reach the assistant.',
        error: true,
      }]);
    } finally {
      setSending(false);
    }
  }

  function send(event) {
    event.preventDefault();
    const text = draft.trim();
    setDraft('');
    sendQuestion(text);
  }

  return (
    <div className={`chatWidget${open ? ' is-open' : ''}`}>
      {open ? (
        <section className="chatWidget-panel" aria-label="Ask Vivosol">
          <header className="chatWidget-header">
            <h2>Ask Vivosol</h2>
            <button type="button" aria-label="Close chat" onClick={() => setOpen(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </header>
          <div className="chatWidget-log" ref={listRef}>
            {messages.length === 0 ? (
              <p className="chatWidget-empty">Ask a question about the plants.</p>
            ) : null}
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={[
                  'chatWidget-message',
                  `is-${message.role}`,
                  message.placeholder ? 'is-placeholder' : '',
                  message.error ? 'is-error' : '',
                ].filter(Boolean).join(' ')}
              >
                {message.role === 'assistant' && !message.error ? (
                  <div
                    className="chatWidget-markdown"
                    dangerouslySetInnerHTML={{ __html: renderChatMarkdown(message.text) }}
                  />
                ) : message.text}
                {message.suggestions?.length ? (
                  <div className="chatWidget-suggestions">
                    {message.suggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={suggestion}
                        disabled={sending}
                        onClick={() => sendQuestion(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            {sending ? <p className="chatWidget-message is-assistant is-pending">Thinking...</p> : null}
          </div>
          <form className="chatWidget-form" onSubmit={send}>
            <label className="visually-hidden" htmlFor="chat-widget-input">Question</label>
            <input
              id="chat-widget-input"
              ref={inputRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ask a question"
              maxLength={4000}
              disabled={sending}
            />
            <button type="submit" disabled={sending || !draft.trim()}>Send</button>
          </form>
        </section>
      ) : null}
      <button
        type="button"
        className="chatWidget-toggle"
        aria-label={open ? 'Close chat' : 'Open chat'}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <FontAwesomeIcon icon={open ? faXmark : faComment} />
      </button>
    </div>
  );
}

export default ChatWidget;
